import { ICompany } from "models/HaulPay";
import type { IOption } from "models/Form";

export type SelfFinanceNoInvoiceToFactoredResult = {
  debtor: ICompany;
  carrierPaymentSpeed: IOption;
  brokersQRFee: IOption;
};

const SELF_FINANCE_NO_INVOICE_TO_FACTORED_MODAL_ID = "self-finance-no-invoice-to-factored";

const createAbortError = () => {
  const error = new Error("The operation was aborted.");
  error.name = "AbortError";

  return error;
};

interface ConfirmSelfFinanceNoInvoiceToFactoredArgs {
  openModal: (modalId: string, props: Record<string, unknown>) => void;
  closeModal?: (modalId: string) => void;
  fromContractType?: string;
  toContractType?: string;
  hasFactoringCompany?: boolean;
  initialPaymentSpeed?: IOption | null;
  initialBrokerSplit?: IOption | null;
  signal?: AbortSignal;
}

export const confirmSelfFinanceNoInvoiceToFactored = ({
  openModal,
  closeModal,
  fromContractType,
  toContractType,
  hasFactoringCompany,
  initialPaymentSpeed,
  initialBrokerSplit,
  signal
}: ConfirmSelfFinanceNoInvoiceToFactoredArgs) =>
  new Promise<SelfFinanceNoInvoiceToFactoredResult>((resolve, reject) => {
    if (signal?.aborted) {
      reject(createAbortError());
      return;
    }

    let isSettled = false;

    const cleanupAbortListener = () => {
      if (signal && handleAbort) {
        signal.removeEventListener("abort", handleAbort);
      }
    };

    const settleResolve = (value: SelfFinanceNoInvoiceToFactoredResult) => {
      if (isSettled) return;

      isSettled = true;
      cleanupAbortListener();
      resolve(value);
    };

    const settleReject = (reason: unknown) => {
      if (isSettled) return;

      isSettled = true;
      cleanupAbortListener();
      reject(reason);
    };

    const handleAbort = () => {
      closeModal?.(SELF_FINANCE_NO_INVOICE_TO_FACTORED_MODAL_ID);
      settleReject(createAbortError());
    };

    signal?.addEventListener("abort", handleAbort, { once: true });

    openModal(SELF_FINANCE_NO_INVOICE_TO_FACTORED_MODAL_ID, {
      fromContractType,
      toContractType,
      hasFactoringCompany,
      initialPaymentSpeed,
      initialBrokerSplit,
      onConfirm: (result: SelfFinanceNoInvoiceToFactoredResult) => {
        if (isSettled || signal?.aborted) {
          return;
        }

        settleResolve(result);
      },
      onCloseModal: () => {
        if (isSettled || signal?.aborted) {
          return;
        }

        settleReject("cancel-edit-fr");
      }
    });
  });
